import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

export default function Notifications() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    dailyReminder: true,
    reminderTime: '20:00',
    weeklyReport: true,
    actionReminders: true,
    forecastAlerts: false,
    microActionReminders: true,
  });

  useEffect(() => {
    // Load settings from local storage
    const stored = localStorage.getItem('tension_notification_settings');
    if (stored) {
      setSettings(JSON.parse(stored));
    }
  }, []);

  const handleToggle = (key: string) => {
    const newSettings = { ...settings, [key]: !settings[key as keyof typeof settings] };
    setSettings(newSettings);
    localStorage.setItem('tension_notification_settings', JSON.stringify(newSettings));
    toast.success('Notification settings updated');
  };

  const handleTimeChange = (time: string) => {
    const newSettings = { ...settings, reminderTime: time };
    setSettings(newSettings);
    localStorage.setItem('tension_notification_settings', JSON.stringify(newSettings));
  };

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <h1 className="text-xl font-light">Notifications</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12 space-y-6">
        {/* Browser Notification Permission */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-neutral-600" />
              <h2 className="text-lg font-medium">Enable Notifications</h2>
            </div>
            <p className="text-sm text-neutral-600">
              Get gentle reminders to complete your daily check-in and stay aware of tension patterns.
            </p>
            <Button
              onClick={() => {
                if ('Notification' in window) {
                  // DEFENSIVE: Wrap Notification API access in try-catch
                  try {
                    Notification.requestPermission().then((permission) => {
                      if (permission === 'granted') {
                        toast.success('Notifications enabled!');
                      } else {
                        toast.error('Notification permission denied');
                      }
                    });
                  } catch (error) {
                    console.debug('Notification API error:', error);
                    toast.error('Notifications not available on this device');
                  }
                } else {
                  toast.error('Notifications not supported in this browser');
                }
              }}
              className="w-full"
            >
              Enable Browser Notifications
            </Button>
          </div>
        </Card>

        {/* Daily Check-in Reminder */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Daily Check-In Reminder</h3>
                <p className="text-sm text-neutral-600">Get reminded to complete your daily tension score check-in</p>
              </div>
              <button
                onClick={() => handleToggle('dailyReminder')}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  settings.dailyReminder ? 'bg-blue-600' : 'bg-neutral-300'
                }`}
              >
                <div
                  className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    settings.dailyReminder ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>

            {settings.dailyReminder && (
              <div className="space-y-2">
                <label className="text-sm text-neutral-700">Reminder Time</label>
                <input
                  type="time"
                  value={settings.reminderTime}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Weekly Report */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Weekly Summary Report</h3>
              <p className="text-sm text-neutral-600">Receive your weekly pattern summary every Sunday</p>
            </div>
            <button
              onClick={() => handleToggle('weeklyReport')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.weeklyReport ? 'bg-blue-600' : 'bg-neutral-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  settings.weeklyReport ? 'translate-x-6' : ''
                }`}
              />
            </button>
          </div>
        </Card>

        {/* Action Reminders */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Action Card Reminders</h3>
              <p className="text-sm text-neutral-600">Get reminders about your daily action recommendations</p>
            </div>
            <button
              onClick={() => handleToggle('actionReminders')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.actionReminders ? 'bg-blue-600' : 'bg-neutral-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  settings.actionReminders ? 'translate-x-6' : ''
                }`}
              />
            </button>
          </div>
        </Card>

        {/* Micro Action Reminders */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Micro-Action Reminders</h3>
              <p className="text-sm text-neutral-600">Receive tiny interventions throughout the day based on your score patterns</p>
            </div>
            <button
              onClick={() => handleToggle('microActionReminders')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                settings.microActionReminders ? 'bg-blue-600' : 'bg-neutral-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  settings.microActionReminders ? 'translate-x-6' : ''
                }`}
              />
            </button>
          </div>
        </Card>

        {/* Forecast Alerts - Premium */}
        <Card className="p-6 bg-neutral-50 border-neutral-300 relative">
          <div className="flex items-center justify-between opacity-50">
            <div>
              <h3 className="font-medium">Forecast Alerts</h3>
              <p className="text-sm text-neutral-600">Get notified when tension is predicted to spike</p>
              <p className="text-xs text-blue-600 mt-1">Premium Feature</p>
            </div>
            <button
              disabled
              className="relative w-12 h-6 rounded-full bg-neutral-300 cursor-not-allowed"
            >
              <div className="absolute top-1 left-1 w-4 h-4 rounded-full bg-white" />
            </button>
          </div>
        </Card>

        {/* Info */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <p className="text-xs text-neutral-700">
            <strong>Privacy Note:</strong> Notifications are sent locally from your device. 
            TENSION does not send push notifications from external servers. Your data never leaves your device.
          </p>
        </Card>
      </div>
    </div>
  );
}